'use client';

import { useState } from 'react';
import { useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { formatDistanceToNow } from 'date-fns';
import { Loader2, Pencil, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { apiErrorMessage } from '@/lib/api/errors';
import { assistantApi } from '@/lib/api/assistant';
import type { AgentConversation, AgentConversationList } from '@/lib/api/types';

/** The backend caps a title at 120 characters. */
const MAX_TITLE_LENGTH = 120;

function when(updatedAt: string): string {
  const parsed = new Date(updatedAt);
  return Number.isNaN(parsed.getTime()) ? '' : formatDistanceToNow(parsed, { addSuffix: true });
}

/**
 * Earlier conversations.
 *
 * Search goes to the server (`?q=`) rather than filtering the page in hand: the
 * list is cursor-paginated, so local filtering would quietly hide every match
 * older than the first page. Reopening runs through `onOpen`, which is
 * `useAssistant().openConversation` — its rejection (a conversation deleted
 * elsewhere, or the network) is caught here so a failed click surfaces a toast
 * instead of doing nothing and leaving an unhandled rejection; the list stays
 * mounted either way so the user can try another row. Rename and delete
 * surface the same toast on failure, leaving their form/dialog open so the
 * user can retry rather than re-finding the row.
 *
 * `activeConversationId` / `onActiveDeleted` exist because this list can delete
 * the conversation the provider still holds open — see the delete mutation.
 */
export function HistoryView({
  onOpen,
  activeConversationId,
  onActiveDeleted,
}: {
  onOpen: (id: string) => void | Promise<void>;
  /** The conversation the provider currently has open, if any. */
  activeConversationId: string | null;
  /** Called when that one is the conversation just deleted. */
  onActiveDeleted: () => void;
}) {
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [renaming, setRenaming] = useState<AgentConversation | null>(null);
  const [title, setTitle] = useState('');
  const [deleting, setDeleting] = useState<AgentConversation | null>(null);

  const { data, isLoading, fetchNextPage, hasNextPage, isFetchingNextPage } = useInfiniteQuery({
    queryKey: ['agent-conversations', search],
    queryFn: ({ pageParam }: { pageParam: string | undefined }): Promise<AgentConversationList> =>
      assistantApi.listConversations(
        pageParam ? { ...(search ? { q: search } : {}), cursor: pageParam } : search ? { q: search } : {},
      ),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => lastPage.next_cursor ?? undefined,
  });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['agent-conversations'] });

  const rename = useMutation({
    mutationFn: ({ id, next }: { id: string; next: string }) => assistantApi.renameConversation(id, next),
    onSuccess: () => {
      setRenaming(null);
      void invalidate();
    },
    onError: (error: unknown) => {
      // Leave the edit form open on failure — the user's typed title is not
      // lost — but say why nothing happened instead of the row just sitting
      // there unresponsive.
      toast.error(apiErrorMessage(error, 'Could not rename that conversation.'));
    },
  });

  const remove = useMutation({
    mutationFn: (id: string) => assistantApi.deleteConversation(id),
    onSuccess: (_result, id) => {
      // Deleting the conversation that is currently open has to reach the
      // provider too: otherwise its id survives in state and in the per-tenant
      // sessionStorage key, `ensureConversation()` hands the next send a dead
      // id, and the backend 404s. The 404 recovery then clears it so the
      // *second* send works — but the composer already discarded the user's
      // text on submit, so the message has to be retyped.
      if (id === activeConversationId) onActiveDeleted();
      setDeleting(null);
      void invalidate();
    },
    onError: (error: unknown) => {
      // Leave the confirm dialog open on failure so the user can retry
      // without re-finding the row and re-opening the dialog.
      toast.error(apiErrorMessage(error, 'Could not delete that conversation.'));
    },
  });

  const conversations = data?.pages.flatMap((page) => page.data) ?? [];

  const handleOpen = (id: string) => {
    // onOpen may be a fire-and-forget mock in tests or the real, rejecting
    // `openConversation` — Promise.resolve() normalizes both so the catch
    // below always runs on a genuine failure and never on a void return.
    Promise.resolve(onOpen(id)).catch((error: unknown) => {
      toast.error(apiErrorMessage(error, 'Could not reopen that conversation.'));
    });
  };

  return (
    <div className="min-h-0 flex-1 overflow-y-auto px-3 py-3">
      <Input
        type="search"
        value={search}
        onChange={(event) => setSearch(event.target.value)}
        placeholder="Search conversations"
        aria-label="Search conversations"
        className="mb-2 h-8 text-sm"
      />

      {isLoading && <Loader2 className="mx-auto my-6 size-4 animate-spin text-muted-foreground" />}

      {!isLoading && conversations.length === 0 && (
        <p className="py-6 text-center text-xs text-muted-foreground">No conversations yet.</p>
      )}

      <ul className="flex flex-col">
        {conversations.map((conversation) => {
          const isRenaming = renaming?.id === conversation.id;

          return (
            <li key={conversation.id} className="group flex items-center gap-2 border-b py-2">
              {isRenaming ? (
                <form
                  className="min-w-0 flex-1"
                  onSubmit={(event) => {
                    event.preventDefault();
                    const next = title.trim();
                    if (next) rename.mutate({ id: conversation.id, next });
                  }}
                >
                  <Input
                    autoFocus
                    value={title}
                    maxLength={MAX_TITLE_LENGTH}
                    aria-label="Conversation title"
                    onChange={(event) => setTitle(event.target.value)}
                    onKeyDown={(event) => {
                      if (event.key === 'Escape') {
                        event.preventDefault();
                        setRenaming(null);
                      }
                    }}
                    className="h-8 text-sm"
                  />
                </form>
              ) : (
                <button
                  type="button"
                  onClick={() => handleOpen(conversation.id)}
                  className="min-w-0 flex-1 text-left"
                >
                  <span className="block truncate text-sm">
                    {conversation.title?.trim() || 'Untitled conversation'}
                  </span>
                  <span className="text-[11px] text-muted-foreground">{when(conversation.updated_at)}</span>
                </button>
              )}

              {!isRenaming && conversation.has_pending_actions && (
                <Badge variant="outline" className="shrink-0 text-[10px]">
                  Waiting
                </Badge>
              )}

              {!isRenaming && (
                <>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="size-8 shrink-0 p-0"
                    aria-label={`Rename ${conversation.title ?? 'conversation'}`}
                    onClick={() => {
                      setRenaming(conversation);
                      setTitle(conversation.title ?? '');
                    }}
                  >
                    <Pencil className="size-3.5" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="size-8 shrink-0 p-0 text-destructive hover:bg-destructive/10 hover:text-destructive"
                    aria-label={`Delete ${conversation.title ?? 'conversation'}`}
                    onClick={() => setDeleting(conversation)}
                  >
                    <Trash2 className="size-3.5" />
                  </Button>
                </>
              )}
            </li>
          );
        })}
      </ul>

      {hasNextPage && (
        <Button
          variant="ghost"
          size="sm"
          className="mt-2 h-8 w-full text-xs"
          disabled={isFetchingNextPage}
          onClick={() => void fetchNextPage()}
        >
          {isFetchingNextPage ? (
            <>
              <Loader2 className="mr-2 size-3.5 animate-spin" />
              Loading...
            </>
          ) : (
            'Load more'
          )}
        </Button>
      )}

      <AlertDialog open={deleting !== null} onOpenChange={(open) => !open && setDeleting(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete conversation</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this conversation? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={remove.isPending}>Cancel</AlertDialogCancel>
            <Button
              variant="destructive"
              disabled={remove.isPending}
              onClick={() => deleting && remove.mutate(deleting.id)}
            >
              {remove.isPending ? (
                <>
                  <Loader2 className="mr-2 size-4 animate-spin" />
                  Deleting...
                </>
              ) : (
                'Delete'
              )}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
